import { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart } from 'recharts';
import { supabase } from '../../../api/supabase';
import Papa from 'papaparse';
import theme from '../../../theme/globalTheme';

const CharterAnalyticsDashboard = () => {
  const [data, setData] = useState([]);
  const [clients, setClients] = useState([]);
  const [charterLinks, setCharterLinks] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [comparisonMode, setComparisonMode] = useState('year');
  const [selectedYears, setSelectedYears] = useState(['2023', '2024', '2025', '2026']);
  
  // Computed analytics state
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyComparison, setYearlyComparison] = useState([]);
  const [charterTypeAnalysis, setCharterTypeAnalysis] = useState([]);
  const [paymentMethodAnalysis, setPaymentMethodAnalysis] = useState([]);
  const [customerSegmentation, setCustomerSegmentation] = useState([]);
  const [seasonalityData, setSeasonalityData] = useState([]);
  const [kpiMetrics, setKpiMetrics] = useState({});
  const [revenueGrowth, setRevenueGrowth] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [averageBookingValue, setAverageBookingValue] = useState([]);
  
  // New charterer analytics state
  const [chartererAnalysis, setChartererAnalysis] = useState([]);
  const [topCharterers, setTopCharterers] = useState([]);
  const [chartererPerformance, setChartererPerformance] = useState([]);
  const [chartererSeasonality, setChartererSeasonality] = useState([]);
  const [chartererMetrics, setChartererMetrics] = useState({});

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, []);

  // Recalculate analytics when data changes
  useEffect(() => {
    if (data.length > 0) {
      calculateAnalytics();
    }
  }, [data, clients, charterLinks, selectedTimeframe]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCombinedData(),
        loadClients(),
        loadCharterLinks()
      ]);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCombinedData = async () => {
    try {
      const filesToLoad = ['2023.csv', '2024.csv', '2025.csv', '2026.csv'];
      const allData = [];
      
      for (const fileName of filesToLoad) {
        const response = await fetch(`https://vlakljvouimjemxbcnmo.supabase.co/storage/v1/object/public/media/${fileName}`);
        
        if (!response.ok) continue;
        
        const text = await response.text();
        
        await new Promise((resolve) => {
          Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => {
              const trimmed = header.trim();
              const headerMap = {
                '': 'purchase_date',
                'DATE': 'purchase_date',
                'MODE DE PAIEMENT': 'payment_method',
                'MOYEN DE PAIEMENT': 'payment_method',
                'TYPE DE CHARTER': 'charter_type',
                'TYPE CHARTER': 'charter_type',
                'DATE CHARTER': 'charter_date',
                'MONTANT TOTAL (CB/ONLINE/FACTURE)': 'total_amount',
                'MONTANT TOTAL': 'total_amount',
                'MONTANT PAYE CASH': 'cash_amount',
                'PRIX DU BATEAU': 'boat_price',
                'TAXES': 'taxes',
                'VIP SERVICES': 'vip_services',
                'NOM CLIENT': 'client_name',
                'NOM PAYEUR': 'payer_name',
                'CHARTERER': 'charterer'
              };
              return headerMap[trimmed] || trimmed.toLowerCase().replace(/\s+/g, '_');
            },
            complete: (results) => {
              const processedData = results.data.map(row => ({
                ...row,
                total_amount: parseEuropeanNumber(row.total_amount) || 0,
                cash_amount: parseEuropeanNumber(row.cash_amount) || 0,
                boat_price: parseEuropeanNumber(row.boat_price) || 0,
                taxes: parseEuropeanNumber(row.taxes) || 0,
                vip_services: parseEuropeanNumber(row.vip_services) || 0,
                purchase_date: row.purchase_date?.trim(),
                charter_date: row.charter_date?.trim(),
                payment_method: row.payment_method?.trim(),
                charter_type: row.charter_type?.trim(),
                client_name: row.client_name?.trim(),
                payer_name: row.payer_name?.trim(),
                charterer: row.charterer?.trim(),
                source_file: fileName,
                year: fileName.replace('.csv', '')
              }));
              
              allData.push(...processedData);
              resolve();
            },
            error: () => resolve()
          });
        });
      }
      
      setData(allData);
    } catch (error) {
      console.error('Erreur lors du chargement CSV:', error);
    }
  };

  const loadClients = async () => {
    try {
      const { data: clientsData, error } = await supabase
        .from('clients')
        .select('*');

      if (!error && clientsData) {
        setClients(clientsData);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  };

  const loadCharterLinks = async () => {
    try {
      const { data: linksData, error } = await supabase
        .from('charter_links')
        .select('*');

      if (!error && linksData) {
        const links = {};
        linksData.forEach(link => {
          links[link.charter_key] = link.client_id;
        });
        setCharterLinks(links);
      } else {
        const savedLinks = localStorage.getItem('charter_client_links');
        if (savedLinks) {
          setCharterLinks(JSON.parse(savedLinks));
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des liens:', error);
    }
  };

  const parseEuropeanNumber = (value) => {
    if (!value || typeof value === 'number') return value;
    
    let cleanValue = value.toString().trim();
    cleanValue = cleanValue.replace(/[€$£¥₹]/g, '');
    cleanValue = cleanValue.trim();
    
    if (cleanValue.includes(',')) {
      cleanValue = cleanValue.replace(/[\s\.]/g, '');
      cleanValue = cleanValue.replace(',', '.');
    } else {
      const parts = cleanValue.split('.');
      if (parts.length > 2 || (parts.length === 2 && parts[1].length > 2)) {
        cleanValue = cleanValue.replace(/\./g, '');
      }
    }
    
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  const convertDateFormat = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return new Date(dateStr);
  };

  const calculateAnalytics = () => {
    if (!data.length) return;

    // Filter data based on timeframe
    const filteredData = selectedTimeframe === 'all' ? data : 
      data.filter(row => row.year === selectedTimeframe);

    calculateMonthlyData(filteredData);
    calculateYearlyComparison();
    calculateCharterTypeAnalysis(filteredData);
    calculatePaymentMethodAnalysis(filteredData);
    calculateCustomerSegmentation(filteredData);
    calculateSeasonalityData();
    calculateKPIMetrics(filteredData);
    calculateRevenueGrowth();
    calculateTopCustomers(filteredData);
    calculateAverageBookingValue();
    
    // New charterer analytics
    calculateChartererAnalysis(filteredData);
    calculateTopCharterers(filteredData);
    calculateChartererPerformance(filteredData);
    calculateChartererSeasonality();
    calculateChartererMetrics(filteredData);
  };

  // New Charterer Analytics Functions
  const calculateChartererAnalysis = (filteredData) => {
    const chartererStats = {};
    
    filteredData.forEach(row => {
      const charterer = row.charterer || 'Non spécifié';
      if (!chartererStats[charterer]) {
        chartererStats[charterer] = {
          name: charterer,
          revenue: 0,
          bookings: 0,
          averageValue: 0,
          customers: new Set(),
          vipRevenue: 0,
          cashRevenue: 0
        };
      }
      
      chartererStats[charterer].revenue += row.total_amount;
      chartererStats[charterer].bookings += 1;
      chartererStats[charterer].customers.add(row.client_name);
      chartererStats[charterer].vipRevenue += row.vip_services;
      chartererStats[charterer].cashRevenue += row.cash_amount;
    });
    
    const chartererArray = Object.values(chartererStats)
      .map(item => ({
        ...item,
        customers: item.customers.size,
        averageValue: item.bookings > 0 ? item.revenue / item.bookings : 0,
        averageCustomerValue: item.customers > 0 ? item.revenue / item.customers : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
    
    setChartererAnalysis(chartererArray);
  };

  const calculateTopCharterers = (filteredData) => {
    const chartererStats = {};
    
    filteredData.forEach(row => {
      const charterer = row.charterer || 'Non spécifié';
      if (!chartererStats[charterer]) {
        chartererStats[charterer] = {
          name: charterer,
          revenue: 0,
          bookings: 0,
          customers: new Set(),
          conversionRate: 0
        };
      }
      
      chartererStats[charterer].revenue += row.total_amount;
      chartererStats[charterer].bookings += 1;
      chartererStats[charterer].customers.add(row.client_name);
    });
    
    const topCharterersList = Object.values(chartererStats)
      .map(item => ({
        ...item,
        customers: item.customers.size,
        averageValue: item.bookings > 0 ? item.revenue / item.bookings : 0,
        revenuePerCustomer: item.customers > 0 ? item.revenue / item.customers : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 20);

    setTopCharterers(topCharterersList);
  };

  const calculateChartererPerformance = (filteredData) => {
    const monthlyChartererStats = {};
    
    filteredData.forEach(row => {
      const date = convertDateFormat(row.charter_date || row.purchase_date);
      if (!date) return;
      
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const charterer = row.charterer || 'Non spécifié';
      
      if (!monthlyChartererStats[monthKey]) {
        monthlyChartererStats[monthKey] = { month: monthKey };
      }
      
      if (!monthlyChartererStats[monthKey][charterer]) {
        monthlyChartererStats[monthKey][charterer] = 0;
      }
      
      monthlyChartererStats[monthKey][charterer] += row.total_amount;
    });
    
    const performanceArray = Object.values(monthlyChartererStats)
      .sort((a, b) => a.month.localeCompare(b.month));
    
    setChartererPerformance(performanceArray);
  };

  const calculateChartererSeasonality = () => {
    const seasonalChartererStats = {};
    
    data.forEach(row => {
      const date = convertDateFormat(row.charter_date || row.purchase_date);
      if (!date) return;
      
      const month = date.getMonth();
      const season = month >= 5 && month <= 8 ? 'Haute saison' : 
                   month >= 3 && month <= 4 || month >= 9 && month <= 10 ? 'Moyenne saison' : 
                   'Basse saison';
      const charterer = row.charterer || 'Non spécifié';
      
      const key = `${season}-${charterer}`;
      if (!seasonalChartererStats[key]) {
        seasonalChartererStats[key] = {
          season,
          charterer,
          revenue: 0,
          bookings: 0
        };
      }
      
      seasonalChartererStats[key].revenue += row.total_amount;
      seasonalChartererStats[key].bookings += 1;
    });
    
    setChartererSeasonality(Object.values(seasonalChartererStats));
  };

  const calculateChartererMetrics = (filteredData) => {
    const chartererData = filteredData.filter(row => row.charterer && row.charterer.trim() !== '');
    const uniqueCharterers = new Set(chartererData.map(row => row.charterer)).size;
    const totalCharterersRevenue = chartererData.reduce((sum, row) => sum + row.total_amount, 0);
    const averageRevenuePerCharterer = uniqueCharterers > 0 ? totalCharterersRevenue / uniqueCharterers : 0;
    
    // Calculate charterer concentration (top 3 charterers percentage)
    const chartererRevenues = {};
    chartererData.forEach(row => {
      const charterer = row.charterer;
      chartererRevenues[charterer] = (chartererRevenues[charterer] || 0) + row.total_amount;
    });
    
    const sortedChartererRevenues = Object.values(chartererRevenues).sort((a, b) => b - a);
    const top3Revenue = sortedChartererRevenues.slice(0, 3).reduce((sum, rev) => sum + rev, 0);
    const chartererConcentration = totalCharterersRevenue > 0 ? (top3Revenue / totalCharterersRevenue) * 100 : 0;
    
    setChartererMetrics({
      uniqueCharterers,
      totalCharterersRevenue,
      averageRevenuePerCharterer,
      chartererConcentration,
      chartererBookings: chartererData.length,
      averageBookingsPerCharterer: uniqueCharterers > 0 ? chartererData.length / uniqueCharterers : 0
    });
  };

  // Existing calculation functions (keeping them as they are)
  const calculateMonthlyData = (filteredData) => {
    const monthlyStats = {};
    
    filteredData.forEach(row => {
      const date = convertDateFormat(row.charter_date || row.purchase_date);
      if (!date) return;
      
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyStats[monthKey]) {
        monthlyStats[monthKey] = {
          month: monthKey,
          year: date.getFullYear(),
          monthName: date.toLocaleDateString('fr-FR', { month: 'long' }),
          revenue: 0,
          bookings: 0,
          averageValue: 0,
          cashRevenue: 0,
          vipRevenue: 0
        };
      }
      
      monthlyStats[monthKey].revenue += row.total_amount;
      monthlyStats[monthKey].bookings += 1;
      monthlyStats[monthKey].cashRevenue += row.cash_amount;
      monthlyStats[monthKey].vipRevenue += row.vip_services;
    });
    
    const monthlyArray = Object.values(monthlyStats)
      .map(item => ({
        ...item,
        averageValue: item.bookings > 0 ? item.revenue / item.bookings : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
    
    setMonthlyData(monthlyArray);
  };

  const calculateYearlyComparison = () => {
    const yearlyStats = {};
    
    data.forEach(row => {
      const year = row.year;
      if (!yearlyStats[year]) {
        yearlyStats[year] = {
          year,
          revenue: 0,
          bookings: 0,
          customers: new Set(),
          averageValue: 0,
          cashRevenue: 0,
          vipRevenue: 0,
          monthlyData: {}
        };
      }
      
      yearlyStats[year].revenue += row.total_amount;
      yearlyStats[year].bookings += 1;
      yearlyStats[year].customers.add(row.client_name);
      yearlyStats[year].cashRevenue += row.cash_amount;
      yearlyStats[year].vipRevenue += row.vip_services;
      
      // Monthly breakdown for each year
      const date = convertDateFormat(row.charter_date || row.purchase_date);
      if (date) {
        const month = date.getMonth();
        if (!yearlyStats[year].monthlyData[month]) {
          yearlyStats[year].monthlyData[month] = { revenue: 0, bookings: 0 };
        }
        yearlyStats[year].monthlyData[month].revenue += row.total_amount;
        yearlyStats[year].monthlyData[month].bookings += 1;
      }
    });
    
    const yearlyArray = Object.values(yearlyStats)
      .map(item => ({
        ...item,
        customers: item.customers.size,
        averageValue: item.bookings > 0 ? item.revenue / item.bookings : 0,
        monthlyData: undefined // Remove Set object for JSON serialization
      }))
      .sort((a, b) => a.year.localeCompare(b.year));
    
    setYearlyComparison(yearlyArray);
  };

  const calculateCharterTypeAnalysis = (filteredData) => {
    const typeStats = {};
    
    filteredData.forEach(row => {
      const type = row.charter_type || 'Non spécifié';
      if (!typeStats[type]) {
        typeStats[type] = {
          name: type,
          revenue: 0,
          bookings: 0,
          averageValue: 0
        };
      }
      
      typeStats[type].revenue += row.total_amount;
      typeStats[type].bookings += 1;
    });
    
    const typeArray = Object.values(typeStats)
      .map(item => ({
        ...item,
        averageValue: item.bookings > 0 ? item.revenue / item.bookings : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
    
    setCharterTypeAnalysis(typeArray);
  };

  const calculatePaymentMethodAnalysis = (filteredData) => {
    const paymentStats = {};
    
    filteredData.forEach(row => {
      const method = row.payment_method || 'Non spécifié';
      if (!paymentStats[method]) {
        paymentStats[method] = {
          name: method,
          revenue: 0,
          bookings: 0,
          percentage: 0
        };
      }
      
      paymentStats[method].revenue += row.total_amount;
      paymentStats[method].bookings += 1;
    });
    
    const totalRevenue = Object.values(paymentStats).reduce((sum, item) => sum + item.revenue, 0);
    
    const paymentArray = Object.values(paymentStats)
      .map(item => ({
        ...item,
        percentage: totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
    
    setPaymentMethodAnalysis(paymentArray);
  };

  const calculateCustomerSegmentation = (filteredData) => {
    const customerStats = {};
    
    filteredData.forEach(row => {
      const customer = row.client_name;
      if (!customerStats[customer]) {
        customerStats[customer] = {
          name: customer,
          revenue: 0,
          bookings: 0,
          averageValue: 0,
          lastBooking: null
        };
      }
      
      customerStats[customer].revenue += row.total_amount;
      customerStats[customer].bookings += 1;
      
      const date = convertDateFormat(row.charter_date || row.purchase_date);
      if (date && (!customerStats[customer].lastBooking || date > customerStats[customer].lastBooking)) {
        customerStats[customer].lastBooking = date;
      }
    });
    
    const customerArray = Object.values(customerStats)
      .map(item => ({
        ...item,
        averageValue: item.bookings > 0 ? item.revenue / item.bookings : 0,
        segment: item.revenue >= 10000 ? 'VIP' : 
                item.revenue >= 5000 ? 'Premium' : 
                item.bookings >= 3 ? 'Fidèle' : 'Standard'
      }))
      .sort((a, b) => b.revenue - a.revenue);
    
    // Group by segments
    const segments = customerArray.reduce((acc, customer) => {
      const segment = customer.segment;
      if (!acc[segment]) {
        acc[segment] = {
          name: segment,
          customers: 0,
          revenue: 0,
          bookings: 0
        };
      }
      
      acc[segment].customers += 1;
      acc[segment].revenue += customer.revenue;
      acc[segment].bookings += customer.bookings;
      
      return acc;
    }, {});
    
    setCustomerSegmentation(Object.values(segments));
  };

  const calculateSeasonalityData = () => {
    const seasonalStats = {};
    
    data.forEach(row => {
      const date = convertDateFormat(row.charter_date || row.purchase_date);
      if (!date) return;
      
      const month = date.getMonth();
      const season = month >= 5 && month <= 8 ? 'Haute saison' : 
                   month >= 3 && month <= 4 || month >= 9 && month <= 10 ? 'Moyenne saison' : 
                   'Basse saison';
      
      if (!seasonalStats[season]) {
        seasonalStats[season] = {
          name: season,
          revenue: 0,
          bookings: 0
        };
      }
      
      seasonalStats[season].revenue += row.total_amount;
      seasonalStats[season].bookings += 1;
    });
    
    setSeasonalityData(Object.values(seasonalStats));
  };

  const calculateKPIMetrics = (filteredData) => {
    const totalRevenue = filteredData.reduce((sum, row) => sum + row.total_amount, 0);
    const totalBookings = filteredData.length;
    const uniqueCustomers = new Set(filteredData.map(row => row.client_name)).size;
    const averageBookingValue = totalBookings > 0 ? totalRevenue / totalBookings : 0;
    const cashPercentage = filteredData.reduce((sum, row) => sum + row.cash_amount, 0) / totalRevenue * 100;
    const vipRevenue = filteredData.reduce((sum, row) => sum + row.vip_services, 0);
    
    // Calculate growth rates
    const currentYear = new Date().getFullYear().toString();
    const lastYear = (new Date().getFullYear() - 1).toString();
    
    const currentYearData = data.filter(row => row.year === currentYear);
    const lastYearData = data.filter(row => row.year === lastYear);
    
    const currentYearRevenue = currentYearData.reduce((sum, row) => sum + row.total_amount, 0);
    const lastYearRevenue = lastYearData.reduce((sum, row) => sum + row.total_amount, 0);
    
    const revenueGrowthRate = lastYearRevenue > 0 ? 
      ((currentYearRevenue - lastYearRevenue) / lastYearRevenue) * 100 : 0;
    
    setKpiMetrics({
      totalRevenue,
      totalBookings,
      uniqueCustomers,
      averageBookingValue,
      cashPercentage,
      vipRevenue,
      revenueGrowthRate,
      customerRetentionRate: calculateRetentionRate(),
      averageCustomerValue: uniqueCustomers > 0 ? totalRevenue / uniqueCustomers : 0
    });
  };

  const calculateRetentionRate = () => {
    const customersByYear = {};
    
    data.forEach(row => {
      const year = row.year;
      if (!customersByYear[year]) {
        customersByYear[year] = new Set();
      }
      customersByYear[year].add(row.client_name);
    });
    
    const years = Object.keys(customersByYear).sort();
    if (years.length < 2) return 0;
    
    const currentYearCustomers = customersByYear[years[years.length - 1]];
    const lastYearCustomers = customersByYear[years[years.length - 2]];
    
    const retainedCustomers = Array.from(currentYearCustomers)
      .filter(customer => lastYearCustomers.has(customer)).length;
    
    return lastYearCustomers.size > 0 ? (retainedCustomers / lastYearCustomers.size) * 100 : 0;
  };

  const calculateRevenueGrowth = () => {
    const growthData = [];
    const years = ['2023', '2024', '2025', '2026'];
    
    years.forEach((year, index) => {
      const yearData = data.filter(row => row.year === year);
      const revenue = yearData.reduce((sum, row) => sum + row.total_amount, 0);
      
      let growth = 0;
      if (index > 0) {
        const prevYearData = data.filter(row => row.year === years[index - 1]);
        const prevRevenue = prevYearData.reduce((sum, row) => sum + row.total_amount, 0);
        growth = prevRevenue > 0 ? ((revenue - prevRevenue) / prevRevenue) * 100 : 0;
      }
      
      growthData.push({
        year,
        revenue,
        growth: Math.round(growth * 100) / 100
      });
    });
    
    setRevenueGrowth(growthData);
  };

  const calculateTopCustomers = (filteredData) => {
    const customerStats = {};
    
    filteredData.forEach(row => {
      const customer = row.client_name;
      if (!customerStats[customer]) {
        customerStats[customer] = {
          name: customer,
          revenue: 0,
          bookings: 0
        };
      }
      
      customerStats[customer].revenue += row.total_amount;
      customerStats[customer].bookings += 1;
    });
    
    const topCustomersList = Object.values(customerStats)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 20);
    
    setTopCustomers(topCustomersList);
  };

  const calculateAverageBookingValue = () => {
    const monthlyABV = {};
    
    data.forEach(row => {
      const date = convertDateFormat(row.charter_date || row.purchase_date);
      if (!date) return;
      
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyABV[monthKey]) {
        monthlyABV[monthKey] = {
          month: monthKey,
          totalRevenue: 0,
          bookings: 0
        };
      }
      
      monthlyABV[monthKey].totalRevenue += row.total_amount;
      monthlyABV[monthKey].bookings += 1;
    });
    
    const abvArray = Object.values(monthlyABV)
      .map(item => ({
        month: item.month,
        averageBookingValue: item.bookings > 0 ? item.totalRevenue / item.bookings : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
    
    setAverageBookingValue(abvArray);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatCurrency = (num) => {
    if (!num) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatPercentage = (num) => {
    return `${Math.round(num * 100) / 100}%`;
  };

  // Chart colors - monochrome only
  const COLORS = {
    primary: theme.colors.white,
    success: theme.colors.lightGrey,
    warning: theme.colors.mediumGrey,
    error: theme.colors.darkGrey,
    purple: theme.colors.textSecondary,
    cyan: theme.colors.textTertiary,
    orange: theme.colors.textSecondary,
    pink: theme.colors.textTertiary
  };

  const PIE_COLORS = [theme.colors.white, theme.colors.lightGrey, theme.colors.mediumGrey, theme.colors.darkGrey, theme.colors.textSecondary, theme.colors.textTertiary];

  return (
    <div style={{ padding: '20px', minHeight: '100vh', background: theme.colors.bgLight }}>
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: theme.borderRadius.card, border: `1px solid ${theme.colors.glassBorder}` }}>
        <h1 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary, fontSize: '32px', fontWeight: '700' }}>
          📊 Dashboard Analytique Yacht
        </h1>
        <p style={{ margin: '0 0 20px 0', color: theme.colors.textSecondary, fontSize: '16px' }}>
          Analyse approfondie des performances commerciales et insights stratégiques
        </p>
        
        {/* Controls */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.colors.textPrimary }}>Période:</label>
            <select 
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              style={{ 
                padding: '8px 12px', 
                borderRadius: '6px', 
                border: `1px solid ${theme.colors.glassBorder}`,
                fontSize: '14px',
                backgroundColor: theme.colors.glassMedium
              }}
            >
              <option value="all">Toutes les années</option>
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: theme.colors.textPrimary }}>Métrique principale:</label>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              style={{ 
                padding: '8px 12px', 
                borderRadius: '6px', 
                border: `1px solid ${theme.colors.glassBorder}`,
                fontSize: '14px',
                backgroundColor: theme.colors.glassMedium
              }}
            >
              <option value="revenue">Chiffre d'affaires</option>
              <option value="bookings">Nombre de réservations</option>
              <option value="averageValue">Valeur moyenne</option>
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ padding: '60px', textAlign: 'center', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', marginBottom: '24px' }}>
          <div style={{ fontSize: '18px', color: theme.colors.white, marginBottom: '12px' }}>⏳ Chargement des données...</div>
          <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>Analyse en cours des performances</div>
        </div>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '24px' }}>
            <div style={{ 
              padding: '24px', 
              backgroundColor: theme.colors.glassMedium, 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: `1px solid ${theme.colors.glassBorder}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '14px', color: theme.colors.white, fontWeight: '600', marginBottom: '4px' }}>💰 Chiffre d'Affaires</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: theme.colors.textPrimary }}>
                    {formatCurrency(kpiMetrics.totalRevenue)}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.colors.white, marginTop: '4px' }}>
                    {kpiMetrics.revenueGrowthRate > 0 ? '↗️' : '↘️'} {formatPercentage(Math.abs(kpiMetrics.revenueGrowthRate))} vs année précédente
                  </div>
                </div>
                <div style={{ fontSize: '36px' }}>💰</div>
              </div>
            </div>

            <div style={{ 
              padding: '24px', 
              backgroundColor: theme.colors.glassMedium, 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: `1px solid ${theme.colors.glassBorder}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '14px', color: theme.colors.white, fontWeight: '600', marginBottom: '4px' }}>🚤 Réservations</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: theme.colors.textPrimary }}>
                    {formatNumber(kpiMetrics.totalBookings)}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                    Moyenne: {formatCurrency(kpiMetrics.averageBookingValue)}
                  </div>
                </div>
                <div style={{ fontSize: '36px' }}>🚤</div>
              </div>
            </div>

            <div style={{ 
              padding: '24px', 
              backgroundColor: theme.colors.glassMedium, 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: `1px solid ${theme.colors.glassBorder}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#722ed1', fontWeight: '600', marginBottom: '4px' }}>👥 Clients</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: theme.colors.textPrimary }}>
                    {formatNumber(kpiMetrics.uniqueCustomers)}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                    Rétention: {formatPercentage(kpiMetrics.customerRetentionRate)}
                  </div>
                </div>
                <div style={{ fontSize: '36px' }}>👥</div>
              </div>
            </div>

            <div style={{ 
              padding: '24px', 
              backgroundColor: theme.colors.glassMedium, 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: `1px solid ${theme.colors.glassBorder}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#fa8c16', fontWeight: '600', marginBottom: '4px' }}>⚓ Charterers</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: theme.colors.textPrimary }}>
                    {formatNumber(chartererMetrics.uniqueCharterers)}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                    CA: {formatCurrency(chartererMetrics.averageRevenuePerCharterer)}
                  </div>
                </div>
                <div style={{ fontSize: '36px' }}>⚓</div>
              </div>
            </div>
          </div>

          {/* Charterer KPI Cards */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 20px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              ⚓ Métriques Charterers
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div style={{ padding: '16px', backgroundColor: '#f6ffed', borderRadius: '8px', border: '1px solid #b7eb8f' }}>
                <div style={{ fontSize: '14px', color: '#389e0d', fontWeight: '600', marginBottom: '4px' }}>Total Charterers</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: theme.colors.textPrimary }}>
                  {formatNumber(chartererMetrics.uniqueCharterers)}
                </div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#fff7e6', borderRadius: '8px', border: '1px solid #ffd591' }}>
                <div style={{ fontSize: '14px', color: '#d46b08', fontWeight: '600', marginBottom: '4px' }}>CA Charterers</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: theme.colors.textPrimary }}>
                  {formatCurrency(chartererMetrics.totalCharterersRevenue)}
                </div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#e6f7ff', borderRadius: '8px', border: '1px solid #91d5ff' }}>
                <div style={{ fontSize: '14px', color: '#0958d9', fontWeight: '600', marginBottom: '4px' }}>Concentration Top 3</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: theme.colors.textPrimary }}>
                  {formatPercentage(chartererMetrics.chartererConcentration)}
                </div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#f9f0ff', borderRadius: '8px', border: '1px solid #d3adf7' }}>
                <div style={{ fontSize: '14px', color: '#722ed1', fontWeight: '600', marginBottom: '4px' }}>Résa/Charterer</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: theme.colors.textPrimary }}>
                  {formatNumber(chartererMetrics.averageBookingsPerCharterer)}
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Trend Chart */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              📈 Évolution du Chiffre d'Affaires Mensuel
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="month" 
                  stroke="#6c757d"
                  fontSize={12}
                />
                <YAxis 
                  yAxisId="revenue"
                  orientation="left"
                  stroke="#6c757d"
                  fontSize={12}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <YAxis 
                  yAxisId="bookings"
                  orientation="right"
                  stroke="#6c757d"
                  fontSize={12}
                />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'revenue' ? formatCurrency(value) : formatNumber(value),
                    name === 'revenue' ? 'Chiffre d\'affaires' : 'Réservations'
                  ]}
                  labelStyle={{ color: theme.colors.textPrimary }}
                  contentStyle={{ 
                    backgroundColor: theme.colors.glassMedium, 
                    border: `1px solid ${theme.colors.glassBorder}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Area
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="revenue"
                  fill={`${COLORS.primary}20`}
                  stroke={COLORS.primary}
                  strokeWidth={3}
                  name="Chiffre d'affaires"
                />
                <Bar
                  yAxisId="bookings"
                  dataKey="bookings"
                  fill={COLORS.success}
                  name="Réservations"
                  opacity={0.8}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px', marginBottom: '24px' }}>
            {/* Charterer Analysis */}
            <div style={{ padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '18px', fontWeight: '600' }}>
                ⚓ Analyse par Charterer
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartererAnalysis.slice(0, 6)}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="revenue"
                    label={({ name, percentage }) => `${name}: ${Math.round(percentage)}%`}
                    labelLine={false}
                  >
                    {chartererAnalysis.slice(0, 6).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Charter Type Analysis */}
            <div style={{ padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '18px', fontWeight: '600' }}>
                🚤 Analyse par Type de Charter
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={charterTypeAnalysis}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="revenue"
                    label={({ name, percentage }) => `${name}: ${Math.round(percentage)}%`}
                    labelLine={false}
                  >
                    {charterTypeAnalysis.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Charterers */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              🏆 Top 20 Charterers
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>#</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Charterer</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Chiffre d'affaires</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Réservations</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Clients</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>CA/Client</th>
                  </tr>
                </thead>
                <tbody>
                  {topCharterers.map((charterer, index) => (
                    <tr key={charterer.name} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, fontWeight: '600' }}>
                        {index + 1}
                        {index === 0 && ' 🥇'}
                        {index === 1 && ' 🥈'}
                        {index === 2 && ' 🥉'}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, fontWeight: '500' }}>
                        {charterer.name}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right', fontWeight: '600', color: theme.colors.white }}>
                        {formatCurrency(charterer.revenue)}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {charterer.bookings}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {charterer.customers}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {formatCurrency(charterer.revenuePerCustomer)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Yearly Comparison */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              📊 Comparaison Annuelle
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={yearlyComparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" stroke="#6c757d" fontSize={12} />
                <YAxis stroke="#6c757d" fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip 
                  formatter={(value, name) => [formatCurrency(value), 'Chiffre d\'affaires']}
                  labelStyle={{ color: theme.colors.textPrimary }}
                  contentStyle={{ 
                    backgroundColor: theme.colors.glassMedium, 
                    border: `1px solid ${theme.colors.glassBorder}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Bar dataKey="revenue" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px', marginBottom: '24px' }}>
            {/* Payment Method Analysis */}
            <div style={{ padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '18px', fontWeight: '600' }}>
                💳 Analyse des Modes de Paiement
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={paymentMethodAnalysis} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#6c757d" fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                  <YAxis type="category" dataKey="name" stroke="#6c757d" fontSize={12} width={100} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="revenue" fill={COLORS.success} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Customer Segmentation */}
            <div style={{ padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '18px', fontWeight: '600' }}>
                👥 Segmentation Clientèle
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {customerSegmentation.map((segment, index) => (
                  <div key={segment.name} style={{ 
                    padding: '20px', 
                    backgroundColor: ['#f6ffed', '#fff7e6', '#e6f7ff', '#f9f0ff'][index] || '#f8f9fa',
                    borderRadius: '8px',
                    border: `1px solid ${['#b7eb8f', '#ffd591', '#91d5ff', '#d3adf7'][index] || '#e1e5e9'}`
                  }}>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: theme.colors.textPrimary, marginBottom: '8px' }}>
                      {segment.name}
                    </div>
                    <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '4px' }}>
                      {segment.customers} clients
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: theme.colors.white }}>
                      {formatCurrency(segment.revenue)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Customers */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              🏆 Top 20 Clients
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>#</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Client</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Chiffre d'affaires</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Réservations</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Moyenne</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.map((customer, index) => (
                    <tr key={customer.name} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, fontWeight: '600' }}>
                        {index + 1}
                        {index === 0 && ' 🥇'}
                        {index === 1 && ' 🥈'}
                        {index === 2 && ' 🥉'}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, fontWeight: '500' }}>
                        {customer.name}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right', fontWeight: '600', color: theme.colors.white }}>
                        {formatCurrency(customer.revenue)}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {customer.bookings}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {formatCurrency(customer.revenue / customer.bookings)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Seasonality Analysis */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              🌊 Analyse de Saisonnalité
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={seasonalityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#6c757d" fontSize={12} />
                <YAxis stroke="#6c757d" fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'revenue' ? formatCurrency(value) : formatNumber(value),
                    name === 'revenue' ? 'Chiffre d\'affaires' : 'Réservations'
                  ]}
                />
                <Bar dataKey="revenue" fill={COLORS.cyan} radius={[4, 4, 0, 0]} />
                <Bar dataKey="bookings" fill={COLORS.orange} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Growth Rate Chart */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              📈 Taux de Croissance Annuel
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={revenueGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" stroke="#6c757d" fontSize={12} />
                <YAxis yAxisId="revenue" orientation="left" stroke="#6c757d" fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                <YAxis yAxisId="growth" orientation="right" stroke="#6c757d" fontSize={12} tickFormatter={(value) => `${value}%`} />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'revenue' ? formatCurrency(value) : `${value}%`,
                    name === 'revenue' ? 'Chiffre d\'affaires' : 'Croissance'
                  ]}
                />
                <Bar yAxisId="revenue" dataKey="revenue" fill={COLORS.primary} radius={[4, 4, 0, 0]} name="Chiffre d'affaires" />
                <Line yAxisId="growth" type="monotone" dataKey="growth" stroke={COLORS.error} strokeWidth={3} name="Croissance %" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Charterer Performance Over Time */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              ⚓ Performance des Charterers dans le Temps
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartererPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#6c757d" fontSize={12} />
                <YAxis stroke="#6c757d" fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: theme.colors.textPrimary }}
                  contentStyle={{ 
                    backgroundColor: theme.colors.glassMedium, 
                    border: `1px solid ${theme.colors.glassBorder}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                {/* Show top 5 charterers as lines */}
                {topCharterers.slice(0, 5).map((charterer, index) => (
                  <Line
                    key={charterer.name}
                    type="monotone"
                    dataKey={charterer.name}
                    stroke={PIE_COLORS[index % PIE_COLORS.length]}
                    strokeWidth={2}
                    name={charterer.name}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Detailed Charterer Analysis Table */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              ⚓ Analyse Détaillée des Charterers
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Charterer</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>CA Total</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Réservations</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>Clients</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>CA Moyen</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>VIP Services</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: theme.colors.textPrimary, border: `1px solid ${theme.colors.glassBorder}` }}>CA/Client</th>
                  </tr>
                </thead>
                <tbody>
                  {chartererAnalysis.map((charterer, index) => (
                    <tr key={charterer.name} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, fontWeight: '500' }}>
                        {charterer.name}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right', fontWeight: '600', color: theme.colors.white }}>
                        {formatCurrency(charterer.revenue)}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {charterer.bookings}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {charterer.customers}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {formatCurrency(charterer.averageValue)}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right', color: '#fa8c16' }}>
                        {formatCurrency(charterer.vipRevenue)}
                      </td>
                      <td style={{ padding: '12px', border: `1px solid ${theme.colors.glassBorder}`, textAlign: 'right' }}>
                        {formatCurrency(charterer.averageCustomerValue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Charterer Seasonal Performance */}
          <div style={{ marginBottom: '24px', padding: '24px', backgroundColor: theme.colors.glassMedium, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 24px 0', color: theme.colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>
              🌊 Performance Saisonnière des Charterers
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {['Haute saison', 'Moyenne saison', 'Basse saison'].map(season => {
                const seasonData = chartererSeasonality.filter(item => item.season === season);
                const topSeasonCharterers = seasonData
                  .sort((a, b) => b.revenue - a.revenue)
                  .slice(0, 5);
                
                return (
                  <div key={season} style={{ 
                    padding: '20px', 
                    backgroundColor: season === 'Haute saison' ? '#f6ffed' : season === 'Moyenne saison' ? '#fff7e6' : '#e6f7ff',
                    borderRadius: '8px',
                    border: `1px solid ${season === 'Haute saison' ? '#b7eb8f' : season === 'Moyenne saison' ? '#ffd591' : '#91d5ff'}`
                  }}>
                    <h4 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary, fontSize: '16px', fontWeight: '600' }}>
                      {season}
                    </h4>
                    {topSeasonCharterers.map((charterer, index) => (
                      <div key={charterer.charterer} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: index < topSeasonCharterers.length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          {charterer.charterer}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: theme.colors.white }}>
                          {formatCurrency(charterer.revenue)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {!loading && data.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          backgroundColor: theme.colors.glassMedium, 
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <p style={{ fontSize: '18px', color: theme.colors.textSecondary, marginBottom: '8px' }}>
            Aucune donnée disponible
          </p>
          <p style={{ fontSize: '14px', color: '#8c8c8c', margin: 0 }}>
            Veuillez charger vos données CSV pour voir les analyses
          </p>
        </div>
      )}
    </div>
    </div>
  );
};

export default CharterAnalyticsDashboard;